/**
 * Padrões institucionais de formatação.
 *
 * O gerador nasce na norma (NBR 14724) e o manual de cada faculdade é uma
 * **camada por cima** dela. Quase sempre a diferença é pequena — uma fonte, um
 * recuo, o lado da legenda da figura —, mas é esse detalhe que faz a
 * coordenação de TCC devolver o trabalho.
 *
 * Três regras valem para tudo aqui:
 *
 * 1. **Nada é inventado.** Cada perfil cita o manual de onde saiu (`fonteDoManual`)
 *    e a data em que foi conferido. Número que não está no manual não entra.
 * 2. **O perfil é forma, não conteúdo.** `aplicarPerfil` mexe em fonte, margem,
 *    recuo, legenda e nas peças exigidas. Ele **nunca** toca no que a pessoa
 *    escreveu: seções, referências, figuras, resumos e identificação ficam
 *    intactos.
 * 3. **Aplicar é reversível.** Trocar de perfil regrava os campos do zero, então
 *    voltar para a norma devolve o documento ao padrão da ABNT.
 *
 * Para acrescentar uma instituição, basta uma entrada em
 * `PERFIS_INSTITUCIONAIS` com os campos que o manual fixa — o que ficar de fora
 * segue a norma. O comentário de cada campo diz em que unidade ele está.
 */
import { ELEMENTOS_DOCUMENTO, fonteDoDocumento, posicaoDaLegenda } from './tipos.js';
import { MARGENS_ABNT_MM, RECUO_DE_PARAGRAFO_MM } from './abnt.js';
export const PERFIS_INSTITUCIONAIS = [
    /* ------------------------------------------------------------------ */
    /* A norma — o padrão de quem não tem manual próprio                   */
    /* ------------------------------------------------------------------ */
    {
        id: 'ABNT',
        sigla: 'ABNT',
        nome: 'Norma ABNT (NBR 14724)',
        descricao: 'O padrão do gerador. Use quando a instituição não tiver manual próprio, ou quando o manual dela só repetir a norma.',
        modelos: ['TRABALHO_ACADEMICO', 'ARTIGO_CIENTIFICO', 'LIVRO'],
        fonteDoManual: 'ABNT NBR 14724:2011 (apresentação de trabalhos acadêmicos), NBR 10520:2002 (citações) e NBR 6023:2018 (referências).',
        exigencias: [
            'Papel A4 branco, retrato.',
            'Margens: 3 cm na esquerda e no topo, 2 cm na direita e no pé.',
            'Fonte Times New Roman 12 no texto; 10 nas citações longas, notas, legendas e referências.',
            'Entrelinha 1,5 no texto; espaço simples nas citações longas, notas e referências.',
            'Recuo da primeira linha do parágrafo em 1,25 cm.',
            'Alinhamento justificado.',
            'Citação com mais de três linhas: recuo de 4 cm, corpo 10, espaço simples e sem aspas.',
            'Numeração de página no canto superior direito, em algarismos arábicos, a partir do texto.',
            'Legenda e fonte da figura **acima** dela.',
        ],
    },
    /* ------------------------------------------------------------------ */
    /* Centro Paula Souza — Fatec e Etec                                   */
    /* ------------------------------------------------------------------ */
    {
        id: 'CPS',
        sigla: 'Fatec / Etec',
        nome: 'Centro Paula Souza',
        descricao: 'Para o TCC das Faculdades de Tecnologia (Fatec) e das Escolas Técnicas (Etec) estaduais de São Paulo.',
        modelos: ['TRABALHO_ACADEMICO'],
        fonteDoManual: 'CENTRO PAULA SOUZA. Manual de normalização de trabalhos acadêmicos. Conforme o exemplar distribuído pelas unidades (manual completo do TCC), conferido em 21/10/2026.',
        exigencias: [
            'Papel branco no tamanho A4.',
            'Margens: 3 cm na superior e na esquerda; 2 cm na inferior e na direita.',
            'Fonte **Arial 12** em todo o trabalho, exceto nas citações diretas com mais de três linhas.',
            'Espaçamento de 1,5 entre as linhas, com 0 (zero) antes e depois do parágrafo.',
            'Início do parágrafo em **3 cm**.',
            'Alinhamento justificado.',
            'A contagem das páginas começa na folha de rosto, mas o número só aparece a partir da introdução.',
            'Figuras numeradas em algarismos arábicos, listadas nos pré-textuais, com o **título na parte inferior** e a fonte logo abaixo.',
            'Tabelas conforme as Normas de Apresentação Tabular do IBGE (1993).',
        ],
        pendencias: [
            'O traço fechado das tabelas no padrão IBGE não é gerado — a tabela sai com as linhas do gerador.',
            'O “espaço vago” entre capítulo, título e subtítulo do manual não tem medida declarada; o gerador usa o espaçamento do formato de capítulo escolhido.',
        ],
        fonte: 'ARIAL',
        margensMm: { ...MARGENS_ABNT_MM },
        recuoDoParagrafoMm: 30,
        legendaDaFigura: 'ABAIXO',
    },
];
/** O perfil pedido, ou `null` se o id não existir (documento antigo, por exemplo). */
export function perfilPorId(id) {
    if (!id)
        return null;
    return PERFIS_INSTITUCIONAIS.find((perfil) => perfil.id === id) ?? null;
}
/** Os perfis que fazem sentido para o modelo escolhido. */
export function perfisDoModelo(modelo) {
    return PERFIS_INSTITUCIONAIS.filter((perfil) => perfil.modelos.includes(modelo));
}
/** O perfil que o documento diz ter escolhido (só para exibir; pode ser `null`). */
export function perfilDoDocumento(documento) {
    return perfilPorId(documento.perfil);
}
/**
 * Aplica o perfil ao documento — **sem tocar no conteúdo**.
 *
 * Os campos que o perfil define são regravados; os que ele não define voltam ao
 * padrão da norma. É isso que faz “trocar de Fatec para ABNT” devolver Times,
 * recuo de 1,25 cm e legenda acima, em vez de deixar o documento num meio-termo
 * que não é norma nenhuma.
 *
 * Quando o id não existe, o documento volta como veio (nada explode, nada se
 * perde).
 */
export function aplicarPerfil(documento, id) {
    const perfil = perfilPorId(id);
    if (!perfil)
        return documento;
    /* As peças exigidas entram na ordem do papel, não na ordem em que foram escritas. */
    const itens = [...documento.itens];
    for (const item of perfil.itensQueAcrescenta ?? []) {
        if (!itens.includes(item))
            itens.push(item);
    }
    itens.sort((a, b) => ELEMENTOS_DOCUMENTO.indexOf(a) - ELEMENTOS_DOCUMENTO.indexOf(b));
    return {
        ...documento,
        perfil: perfil.id,
        itens,
        fonte: perfil.fonte ?? 'TIMES',
        margensMm: perfil.margensMm ? { ...perfil.margensMm } : null,
        recuoDoParagrafoMm: perfil.recuoDoParagrafoMm ?? null,
        legendaDaFigura: perfil.legendaDaFigura ?? 'ACIMA',
    };
}
/**
 * O que a pessoa mudou depois de aplicar o padrão.
 *
 * Serve para o editor avisar sem brigar: “seu documento está no padrão Fatec,
 * menos a fonte”. Lista vazia = documento igual ao manual.
 */
export function divergenciasDoPerfil(documento) {
    const perfil = perfilDoDocumento(documento);
    if (!perfil)
        return [];
    const mudou = [];
    if (fonteDoDocumento(documento) !== (perfil.fonte ?? 'TIMES'))
        mudou.push('a fonte do texto');
    const esperadas = perfil.margensMm ?? MARGENS_ABNT_MM;
    const atuais = documento.margensMm ?? MARGENS_ABNT_MM;
    const margensIguais = atuais.esquerda === esperadas.esquerda &&
        atuais.topo === esperadas.topo &&
        atuais.direita === esperadas.direita &&
        atuais.fundo === esperadas.fundo;
    if (!margensIguais)
        mudou.push('as margens');
    const recuoEsperado = perfil.recuoDoParagrafoMm ?? RECUO_DE_PARAGRAFO_MM;
    const recuoAtual = Number(documento.recuoDoParagrafoMm ?? RECUO_DE_PARAGRAFO_MM);
    if (Math.abs(recuoAtual - recuoEsperado) > 0.01)
        mudou.push('o recuo do parágrafo');
    if (posicaoDaLegenda(documento) !== (perfil.legendaDaFigura ?? 'ACIMA'))
        mudou.push('o lado da legenda da figura');
    return mudou;
}
//# sourceMappingURL=perfis.js.map